import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    model,
    inject,
    ViewEncapsulation,
    output,
} from '@angular/core';
import { ITodoItem } from '@fi-app/fi-onboarding/fi-todo/fi-todo';
import { IOnboardingCompletionUserStep, ITodoItemCompletion } from '../../../../fi-onboarding-completion';
import { FiTooltipButtonIconColors } from '@fi-shared/fi-icon-tooltip-button/fi-icon-tooltip-button.component';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { FiInsertMedia2Service } from '@fi-app/shared/fi-insert-media/fi-insert-media2.service';
import { ObjectTypes } from '@fi-shared/types';
import { IInsertMediaResult } from '@fi-shared/fi-insert-media/fi-insert-media';
import { FiOnboardingCompletionStateType } from '../../../../fi-onboarding-completion-enums';
import { isNullOrUndefined } from '@fi-app/shared/utils/is-null-or-undefined';
import { FiOnboardingStepCompletionDialogState } from '../../../../fi-onboarding-step-completion-dialog/fi-onboarding-step-completion-dialog-state.service';

@Component({
    selector: 'fi-todo-list-completion-item',
    templateUrl: './todo-list-item.component.html',
    styleUrl: './todo-list-item.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-todo-list-completion-item' },
    standalone: false,
})
export class FiTodoListItemComponent {
    public todoItem = input.required<ITodoItem>();
    public userCompletionStep = model.required<IOnboardingCompletionUserStep>();
    public nextStepCompletionState = input.required<FiOnboardingCompletionStateType>();
    public isTodoItemCompletionChanged = output<boolean>();
    protected readonly FiTooltipButtonIconColors = FiTooltipButtonIconColors;

    private insertMedia2Service = inject(FiInsertMedia2Service);
    private readonly completionDialogState = inject(FiOnboardingStepCompletionDialogState);

    protected isCompletionDisabled = computed(() => {
        if (this.completionDialogState.isReportMode()) {
            return true;
        }
        const nextStepCompletionState = this.nextStepCompletionState();
        const currentStepCompleted =
            this.userCompletionStep().CompletionState === FiOnboardingCompletionStateType.Completed;
        if (isNullOrUndefined(nextStepCompletionState)) {
            return currentStepCompleted;
        }
        return nextStepCompletionState !== FiOnboardingCompletionStateType.NotCompleted && currentStepCompleted;
    });

    protected todoListItemCompletion = computed(() =>
        this.userCompletionStep().TodoItemCompletions?.find((item) => item.TodoItemId === this.todoItem().Id),
    );

    protected onCheckboxChange(isCompleted: boolean): void {
        this.updateTodoListItem({ IsCompleted: isCompleted });
    }

    protected onCommentChange(commentContent: string): void {
        const currentItem = this.todoListItemCompletion();
        if (currentItem) {
            this.updateTodoListItem({
                Comments: [{ ...currentItem.Comments[0], Content: commentContent }],
            });
        }
    }

    protected openInsertMediaDialog(): void {
        this.insertMedia2Service
            .openInsertMediaDialogWithFileSizes(ObjectTypes.TodoItem)
            .onYesButtonClick((result: IInsertMediaResult) => {
                const totalMediaBankFileReferences = [
                    ...this.todoListItemCompletion().MediaBankFileReferences,
                    ...result.mediaBankFileReferences,
                ];
                this.onMediaBankFileReferencesChange(totalMediaBankFileReferences);
            });
    }

    protected onMediaBankFileReferencesChange(mediaBankFileReferences: IMediaBankFileReference[]): void {
        this.updateTodoListItem({ MediaBankFileReferences: mediaBankFileReferences });
    }

    private updateTodoListItem(updates: Partial<ITodoItemCompletion>): void {
        this.userCompletionStep.update((step) => ({
            ...step,
            CompletionState: FiOnboardingCompletionStateType.InProgress,
            TodoItemCompletions: step.TodoItemCompletions?.map((item) =>
                item.TodoItemId === this.todoItem().Id ? { ...item, ...updates } : item,
            ),
        }));
        this.isTodoItemCompletionChanged.emit(true);
    }
}
